import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'

import API from '../../component/Axios'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import ListEmptyState from '../../component/ListEmptyState'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import Title from '../../component/Title'
import { getLocationState } from '../../utils/listPageState'
import {
    applyAppPopupDraft,
    clearAppPopupDraft,
    createEditAppPopupDraftKey,
    readAppPopupDraft,
} from './appPopupDraftUtils'
import AppPopupFormPage from './AppPopupFormPage'
import useAppPopupFormFlow from './useAppPopupFormFlow'

// 로딩/오류 상태에서도 같은 snackbar 표시 규칙을 쓰도록 묶는다.
function AppPopupSnackbar({ snackbar }) {
    const {
        open,
        severity,
        message,
        onClose,
    } = snackbar

    if (!open) {
        return null
    }

    return <Snackbars open={open} severity={severity} message={message} handleClose={onClose}/>
}

// 수정 화면에서 상세 API 응답을 기다리는 동안 spinner와 오류 snackbar를 표시한다.
function AppPopupLoadingState({ snackbar }) {
    return (
        <Grid item xs={12}>
            <Spiner/>
            <AppPopupSnackbar snackbar={snackbar} />
        </Grid>
    )
}

// 수정 대상 상세를 불러오지 못했을 때 목록 복귀 액션과 안내 메시지를 표시한다.
function AppPopupDetailErrorState({
    listState,
    snackbar,
    onBackClick,
    detailErrorMessage,
}) {
    const {
        search,
        curPage,
        searchSelect,
    } = listState

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 랜덤 팝업 수정</Title>
                <ListEmptyState
                    variant="error"
                    title="App 랜덤 팝업 상세를 불러오지 못했습니다."
                    description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
                    sx={{ mt: 2 }}
                />
                <BottomButtonGroup
                    path="/appPopups"
                    search={search}
                    curPage={curPage}
                    searchSelect={searchSelect}
                    visible={false}
                    onBackClick={onBackClick}
                />
                <AppPopupSnackbar snackbar={snackbar} />
            </Paper>
        </Grid>
    )
}

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    deviceType: 'all',
    type: 1,
    status: 1,
    landingUrl: '',
})

export default function AppPopup(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.popupId
    const listCurPage = locationState.curPage || 1
    const listSearch = location.search || ''
    const listSearchSelect = locationState.searchSelect
    const draftKey = createEditAppPopupDraftKey(id)
    const storedDraftRef = useRef(readAppPopupDraft(draftKey))
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')

    const listState = {
        search: listSearch,
        curPage: listCurPage,
        searchSelect: listSearchSelect,
    }

    const showError = useCallback((message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar('error')
        setMessageSnackbar(message)
    }, [])

    const {
        setInitialDraftSnapshot,
        restorePickerState,
        leaveGuardEnabled,
        productPicker,
        handlers,
    } = useAppPopupFormFlow({
        draftKey,
        draftEnabled: draftReady && hasDetail,
        params,
        setParams,
        setImage,
        saving,
        setSaving,
        history,
        listState,
        saveConfig: {
            endpoint: '/api/v1/admin/appPopup/update',
            popupId: id,
            failureMessage: 'App 랜덤 팝업을 저장하지 못했습니다.',
        },
        pickerConfig: {
            initialPage: storedDraftRef.current?.pickerPage,
            initialSearchText: storedDraftRef.current?.pickerSearchText,
            initialSearchSelect: locationState.searchSelect,
        },
        showError,
    })

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 App 랜덤 팝업 상세를 불러올 수 없습니다.'
                setParams(createDefaultParams())
                setImage('')
                setHasDetail(false)
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                showError(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appPopup/getDetail', { popupId: id })
                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.popupId) {
                        setParams(createDefaultParams())
                        setImage('')
                        setDetailErrorMessage('App 랜덤 팝업 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const storedDraft = readAppPopupDraft(draftKey)
                    storedDraftRef.current = storedDraft
                    const restoredDraft = applyAppPopupDraft({
                        baseParams,
                        basePickerPage: 1,
                        basePickerSearchText: '',
                        draft: storedDraft,
                    })

                    setInitialDraftSnapshot({
                        params: baseParams,
                        pickerPage: 1,
                        pickerSearchText: '',
                    })

                    setParams(restoredDraft.params)
                    restorePickerState({
                        page: restoredDraft.pickerPage,
                        searchText: restoredDraft.pickerSearchText,
                    })
                    setImage(baseParams.image === null ? '/images/noimage.jpg' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}`)
                    setHasDetail(true)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'App 랜덤 팝업 상세를 불러오지 못했습니다.'
                    setParams(createDefaultParams())
                    setImage('')
                    setDetailErrorMessage(errorMessage)
                    showError(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'App 랜덤 팝업 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setImage('')
                setDetailErrorMessage(errorMessage)
                showError(errorMessage)
            }

            setLoading(false)
        }

        getData()
    }, [draftKey, history, id, restorePickerState, setInitialDraftSnapshot, showError])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const snackbar = {
        open: openSnackbar,
        severity: severitySnackbar,
        message: messageSnackbar,
        onClose: handleClose,
    }

    if (loading) {
        return <AppPopupLoadingState snackbar={snackbar} />
    }

    if (!hasDetail) {
        return (
            <AppPopupDetailErrorState
                listState={listState}
                snackbar={snackbar}
                onBackClick={() => clearAppPopupDraft(draftKey)}
                detailErrorMessage={detailErrorMessage}
            />
        )
    }

    return (
        <AppPopupFormPage
            title="App 랜덤 팝업 수정"
            params={params}
            image={image}
            saving={saving}
            leaveGuardEnabled={leaveGuardEnabled}
            listState={listState}
            snackbar={snackbar}
            productPicker={productPicker}
            onSubmit={handlers.onSubmit}
            onImageChange={handlers.onImageChange}
            onFieldChange={handlers.onFieldChange}
            onBackClick={handlers.onBackClick}
        />
    )
}
